import processInput from 'apps/web/utils/formInput';

import {
  LC_PAGE_SUBJECT_CHAR_LIMIT,
  SUMMATIVETEST_ASSESSMENTTYPE_OPTIONS,
  UP_PROMPT_NAME_MAP,
} from '@lessonPlanner/constants';
import { UnitPlanPromptNames } from '@lessonPlanner/constants';

import { MultiSelectField, SelectField, TextField } from '../form-fields';
import TopicsSection from '../organisms/TopicsSection';
import { GradeLevelDropdown } from '../Select';

import FORM_KEYS from '../../constants/forms/summative-test/formKeys';
import { getFirstGradeLevelForCurriculum } from '../../utils/curriculumHelpers';

export default function InternationalST({
  errors,
  watchedValues,
  setValue,
  triggerValidation,
  formState,
  topicsList,
}: {
  errors;
  watchedValues;
  setValue;
  triggerValidation;
  formState;
  topicsList;
}) {
  const {
    subject,
    gradeLevel,
    topics = [],
    assessmentTypes,
    curriculum,
  } = watchedValues;
  const { touchedFields, submitCount } = formState.formState || {
    touchedFields: {},
    submitCount: 0,
  };

  const handleMultipleSelectChange = (value) => {
    setValue(FORM_KEYS.assessmentTypes, value);
    if (value.length > 0) {
      setValue(FORM_KEYS.assessmentTypes, value, { shouldValidate: true });
    }
    topicsList.debouncedValidation(FORM_KEYS.assessmentTypes);
  };

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  const isAssessmentTypesInvalid =
    assessmentTypes?.length === 0 &&
    touchedFields?.assessmentTypes &&
    submitCount > 0;

  return (
    <>
      <SelectField
        className="col-span-full"
        error={!!errors.curriculum}
        errorMessage={!!errors.curriculum && 'Please select the Curriculum'}
        id={FORM_KEYS.curriculum}
        label="Curriculum"
        name={FORM_KEYS.curriculum}
        value={curriculum}
        onBlur={() => triggerValidation(FORM_KEYS.curriculum)}
        onChange={(value) => {
          updateFieldValue(FORM_KEYS.curriculum, value);
          const firstGradeLevelOption = getFirstGradeLevelForCurriculum(
            value as UnitPlanPromptNames,
          );
          updateFieldValue(FORM_KEYS.gradeLevel, firstGradeLevelOption);
        }}
      >
        {Object.values(UnitPlanPromptNames).map((format) => {
          return (
            <option key={format} value={format}>
              {UP_PROMPT_NAME_MAP[format]}
            </option>
          );
        })}
      </SelectField>

      <GradeLevelDropdown
        curriculum={curriculum as UnitPlanPromptNames}
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        isCurriculumBased={true}
        value={gradeLevel}
        onBlur={() => topicsList.triggerGradualValidation(FORM_KEYS.gradeLevel)}
        onChange={(value) => updateFieldValue(FORM_KEYS.gradeLevel, value)}
      />
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id={FORM_KEYS.subject}
        isMaxLengthReached={subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT}
        label="Subject"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name={FORM_KEYS.subject}
        placeholder={'Input the subject for this test'}
        type="subject"
        value={subject}
        onBlur={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            e.target.value,
          );
        }}
        onChange={(e) => {
          processInput(
            updateFieldValue,
            triggerValidation,
            FORM_KEYS.subject,
            e.target.value,
          );
        }}
      />

      <MultiSelectField
        className="col-span-full"
        id={FORM_KEYS.assessmentTypes}
        label="Assessment Type/s"
        options={SUMMATIVETEST_ASSESSMENTTYPE_OPTIONS}
        value={assessmentTypes || []}
        error={
          !!errors.assessmentTypes ||
          (isAssessmentTypesInvalid && assessmentTypes?.length === 0)
        }
        errorMessage={
          (!!errors.assessmentTypes && errors.assessmentTypes?.message) ||
          (isAssessmentTypesInvalid &&
            assessmentTypes?.length === 0 &&
            'Please select at least one Assessment Type')
        }
        onChange={handleMultipleSelectChange}
      />

      <TopicsSection
        addSubtopic={topicsList.addSubtopic}
        addTopic={topicsList.addTopic}
        emptySubtopicsIndex={topicsList.emptySubtopicsIndex}
        errors={errors}
        handleSubtopicChange={topicsList.handleSubtopicChange}
        handleTrimUpdate={topicsList.handleTrimUpdate}
        isTopicEmpty={topicsList.isTopicEmpty}
        removeSubtopic={topicsList.removeSubtopic}
        removeTopic={topicsList.removeTopic}
        showSubtopics={topicsList.showSubtopics}
        toggleSubtopicsVisibility={topicsList.toggleSubtopicsVisibility}
        topics={topics}
        handleTopicChange={(topicIndex, value) => {
          topicsList.handleTopicChange(topicIndex, value);
          topicsList.debouncedValidation(FORM_KEYS.topics);
        }}
      />
    </>
  );
}
